import { Inject, Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { BackUrlParams } from '../models/back-url-params.model';
import { Router } from '@angular/router';
import {
  CHECKOUT_STEPS_ROUTE,
  FeatureToggleEnum,
  ORDER_LINK,
  ORDER_STEP_ADDRESS,
  ORDER_STEP_THANKYOU,
} from '~shared/constants';
import { APP_BASE_HREF } from '@angular/common';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { tap } from 'rxjs/operators';
import { ServerLoggerService } from '~shared/modules/server-logger/services/server-logger.service';
import { CheckoutCommonUtil } from '~app/modules/checkout/utils/checkout-common-util';

@Injectable({
  providedIn: 'root',
})
export class StepperService {
  private selectStepSource = new Subject<string>();
  private previousStepSource = new Subject<string>();
  private nextStepSource = new Subject<string>();
  private backUrlParams: BackUrlParams;

  selectStepSource$ = this.selectStepSource.asObservable();
  previousStep$ = this.previousStepSource.asObservable();
  nextStep$ = this.nextStepSource.asObservable();

  constructor(
    private router: Router,
    private readonly featureFlagService: ActiveFeaturesConfigcatService,
    private serverLogger: ServerLoggerService,
    @Inject(APP_BASE_HREF) private baseHref: string
  ) {}

  getBackUrlParams() {
    const logOrderNumber = CheckoutCommonUtil.getOrderNumberFromLocalForLogging();
    const sessionId = CheckoutCommonUtil.getSessionIdFromSessionStorage();

    this.serverLogger.info('Checkout - FE - go back triggered', {
      logOrderNumber,
      sessionId,
    });

    return this.backUrlParams;
  }

  setBackUrlParams(backUrl: BackUrlParams) {
    this.backUrlParams = backUrl;
  }

  nextStep() {
    const logOrderNumber = CheckoutCommonUtil.getOrderNumberFromLocalForLogging();
    const sessionId = CheckoutCommonUtil.getSessionIdFromSessionStorage();

    this.serverLogger.info('Checkout - FE - go to next step triggered', {
      logOrderNumber,
      sessionId,
    });

    this.nextStepSource.next();
  }

  prevStep() {
    const logOrderNumber = CheckoutCommonUtil.getOrderNumberFromLocalForLogging();
    const sessionId = CheckoutCommonUtil.getSessionIdFromSessionStorage();

    this.serverLogger.info('Checkout - FE - go to previous step triggered', {
      logOrderNumber,
      sessionId,
    });

    this.previousStepSource.next();
  }

  goToStep(stepIndex) {
    const logOrderNumber = CheckoutCommonUtil.getOrderNumberFromLocalForLogging();
    const sessionId = CheckoutCommonUtil.getSessionIdFromSessionStorage();

    this.serverLogger.info(`Checkout - FE - go to step ${stepIndex} triggered`, {
      logOrderNumber,
      sessionId,
    });

    this.selectStepSource.next(stepIndex);
  }

  goToThankYouPage(logOrderNumber) {
    const sessionId = CheckoutCommonUtil.getSessionIdFromSessionStorage();

    this.serverLogger.info('Checkout - FE - go to thank you page triggered', {
      logOrderNumber,
      sessionId,
    });

    this.router.navigate([ORDER_LINK, ORDER_STEP_THANKYOU], { queryParams: { 'order-number': logOrderNumber } });
  }

  goToAddressStep() {
    const logOrderNumber = CheckoutCommonUtil.getOrderNumberFromLocalForLogging();
    const sessionId = CheckoutCommonUtil.getSessionIdFromSessionStorage();

    this.serverLogger.info('Checkout - FE - go address step triggered', {
      logOrderNumber,
      sessionId,
    });

    return this.featureFlagService
      .isFeatureEnabledReadyForCurrentCountry$(FeatureToggleEnum.FF_EXTERNAL_CHECKOUT_ADDRESS_STEP)
      .pipe(
        tap((isEnabled) => {
          if (isEnabled) {
            const location = `/${this.baseHref.replace(
              new RegExp('/', 'g'),
              ''
            )}/${CHECKOUT_STEPS_ROUTE}/${ORDER_STEP_ADDRESS}`;
            window.location.href = location;
            return;
          }

          this.router.navigate([ORDER_LINK, ORDER_STEP_ADDRESS]);
        })
      );
  }
}
